import type { Book, BookLibrary } from "./types";
import type { SortOption } from "../ui/state";

/** Create a new empty library */
export function createEmptyLibrary(): BookLibrary {
  return {
    version: 1,
    lastModified: new Date().toISOString(),
    books: [],
  };
}

/** Generate a UUID v4 */
export function generateId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    try {
      return crypto.randomUUID();
    } catch {
      // Falls through to fallback (non-secure context)
    }
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** Create a new blank book with defaults */
export function createNewBook(partial?: Partial<Book>): Book {
  return {
    id: generateId(),
    title: "",
    author: "",
    datePublished: "",
    status: "want-to-read",
    recommendations: [],
    dateAdded: new Date().toISOString(),
    ...partial,
  };
}

/** Sort books by the given sort option */
export function sortBooksBy(books: Book[], option: SortOption): Book[] {
  switch (option) {
    case "dateRead":
      return sortByDateRead(books);
    case "series":
      return sortBySeries(books);
    case "title":
      return sortByTitle(books);
  }
}

/** reading → read with date (newest) → read without date → want-to-read */
function sortByDateRead(books: Book[]): Book[] {
  const reading = books.filter((b) => b.status === "reading");
  const readDated = books.filter((b) => b.status === "read" && b.dateRead);
  const readUndated = books.filter((b) => b.status === "read" && !b.dateRead);
  const wantToRead = books.filter((b) => b.status === "want-to-read");

  reading.sort((a, b) => a.title.localeCompare(b.title));
  readDated.sort((a, b) => (b.dateRead! > a.dateRead! ? 1 : b.dateRead! < a.dateRead! ? -1 : 0));
  readUndated.sort((a, b) => a.title.localeCompare(b.title));
  wantToRead.sort((a, b) => a.title.localeCompare(b.title));

  return [...reading, ...readDated, ...readUndated, ...wantToRead];
}

/** Series books by name then number, non-series books by title at bottom */
function sortBySeries(books: Book[]): Book[] {
  const withSeries = books.filter((b) => b.series);
  const withoutSeries = books.filter((b) => !b.series);

  withSeries.sort((a, b) => {
    const seriesCmp = a.series!.localeCompare(b.series!);
    if (seriesCmp !== 0) return seriesCmp;
    const numA = parseFloat(a.seriesNumber ?? "");
    const numB = parseFloat(b.seriesNumber ?? "");
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return (a.seriesNumber ?? "").localeCompare(b.seriesNumber ?? "");
  });

  withoutSeries.sort((a, b) => a.title.localeCompare(b.title));

  return [...withSeries, ...withoutSeries];
}

function sortByTitle(books: Book[]): Book[] {
  return [...books].sort((a, b) => a.title.localeCompare(b.title));
}

export function filterBooks(books: Book[], query: string): Book[] {
  if (!query.trim()) return books;
  const lower = query.toLowerCase();
  return books.filter(
    (b) =>
      b.title.toLowerCase().includes(lower) ||
      b.author.toLowerCase().includes(lower) ||
      (b.series && b.series.toLowerCase().includes(lower)) ||
      (b.isbn && b.isbn.includes(lower)),
  );
}
